import { Sparkles, AlertTriangle, Gauge, Layers3, Ruler, Box, Axis3d, ArrowRight, Info } from "lucide-react";
import { pub } from "@/lib/pubThemeClasses";
import { useLanguage } from "@/context/LanguageContext";

/** Tam eşleşen buildAnalysis() Türkçe çıktıları → i18n anahtarı (EN görünüm). */
const TR_ANALYSIS_TO_KEY = {
  İncelemede: "ai.val.printability.pending",
  "Uygun görünüyor": "ai.val.printability.ok",
  "STEP alındı • önizleme STL/OBJ için desteklenir": "ai.val.printability.stepPreview",
  "FDM (varsayılan) — hassas detay için SLA": "ai.val.method.fdmDefault",
  "SLA / DLP (yüksek çözünürlük)": "ai.val.method.slaDetail",
  "Mühendislik inceleme sonrası belirlenir": "ai.val.method.afterReview",
  "PLA / PETG • dayanım için ABS veya Nylon": "ai.val.materials.plaPetg",
  "Uygulama alanına göre önerilecek": "ai.val.materials.byUseCase",
  "Teklif formunu gönderin, teknik ekip dosyanızı inceleyip size dönüş sağlasın.": "ai.val.nextStep",
  "Parça büyük boyutlu: parçalı üretim veya büyük hacim yatak gerekebilir.": "ai.val.risk.largePart",
  "Çok küçük ölçek: SLA reçine tercih edilmeli, detay kaybı riski.": "ai.val.risk.smallScale",
  "İnce duvarlar veya destek gereksinimi üretim öncesi kontrol edilmelidir.": "ai.val.risk.thinWalls",
  "STEP parametrik format — nihai değerlendirme teknik ekibimizce yapılacak.": "ai.val.risk.stepFile",
};

const ORIENT_TR_TO_KEY = {
  "XY tabanı (Z yukarı)": "ai.val.orient.xy",
  "XZ tabanı (Y yukarı)": "ai.val.orient.xz",
  "YZ tabanı (X yukarı)": "ai.val.orient.yz",
};

/**
 * @param {string | null | undefined} raw
 * @param {'tr'|'en'} language
 * @param {(k: string) => string} t
 */
function translateAnalysisText(raw, language, t) {
  if (raw == null || raw === "") return raw;
  if (raw === "—") return "—";
  if (language !== "en") return raw;
  const direct = TR_ANALYSIS_TO_KEY[raw];
  if (direct) return t(direct);
  const meshIdx = raw.indexOf(" Mesh yoğunluğu");
  if (meshIdx !== -1) {
    const baseTr = raw.slice(0, meshIdx).trim();
    const baseKey = TR_ANALYSIS_TO_KEY[baseTr];
    const baseOut = baseKey ? t(baseKey) : baseTr;
    return `${baseOut} ${t("ai.val.risk.meshDense")}`;
  }
  const ok = ORIENT_TR_TO_KEY[raw];
  if (ok) return t(ok);
  return raw;
}

/**
 * Premium AI Pre-Analysis panel.
 * Accepts the `analysis` object produced by lib/modelAnalysis.buildAnalysis.
 * Designed to later connect to real geometry analysis without layout change.
 */
export default function AIAnalysisPanel({ analysis, loading }) {
  const { t, language } = useLanguage();
  if (!analysis && !loading) return null;

  return (
    <div
      data-testid="ai-analysis-panel"
      className={`border bg-[#0C0C0F] relative overflow-hidden ${pub.borderSubtle}`}
    >
      {/* subtle top border accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FF4F00] to-transparent opacity-60" />

      <div className={`flex items-center justify-between px-5 py-4 border-b ${pub.panelInnerDivide}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border border-[#FF4F00]/40 bg-[#FF4F00]/10 grid place-items-center">
            <Sparkles className="w-4 h-4 text-[#FF4F00]" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-display font-bold uppercase tracking-tight text-sm">{t("ai.panel.title")}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-0.5">{t("ai.panel.subtitle")}</div>
          </div>
        </div>
        {analysis && (
          <span
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${
              analysis.printabilityTone === "good"
                ? "text-[#00FF9D] border-[#00FF9D]/40 bg-[#00FF9D]/5"
                : analysis.printabilityTone === "warn"
                ? "text-amber-300 border-amber-400/40 bg-amber-400/5"
                : "text-[#00E5FF] border-[#00E5FF]/40 bg-[#00E5FF]/5"
            }`}
          >
            {translateAnalysisText(analysis.printability, language, t)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="p-6 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t("ai.loading")}</div>
      ) : (
        <>
          {/* Core fields */}
          <div className="divide-y divide-white/5">
            <Row label={t("ai.row.fileType")} value={analysis.fileType} />
            <Row
              label={t("ai.row.printability")}
              value={translateAnalysisText(analysis.printability, language, t)}
              accent={analysis.printabilityTone}
            />
            <Row label={t("ai.row.method")} value={translateAnalysisText(analysis.method, language, t)} />
            <Row label={t("ai.row.materials")} value={translateAnalysisText(analysis.materials, language, t)} />
            <Row
              label={t("ai.row.risks")}
              value={translateAnalysisText(analysis.risks, language, t)}
              icon={<AlertTriangle className="w-3.5 h-3.5 text-amber-400" />}
              multiline
            />
            <Row
              label={t("ai.row.nextStep")}
              value={translateAnalysisText(analysis.nextStep, language, t)}
              icon={<ArrowRight className="w-3.5 h-3.5 text-[#FF4F00]" />}
              multiline
            />
          </div>

          {/* Technical metadata */}
          {analysis.meta && (
            <div className={`border-t bg-[#050505] p-5 ${pub.panelInnerDivide}`}>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2">
                <Info className="w-3 h-3" /> {t("ai.meta.section")}
              </div>
              <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/5">
                <MetaCell
                  icon={<Ruler className="w-3.5 h-3.5" />}
                  label={t("ai.meta.dimensions")}
                  value={analysis.meta.dimensions}
                />
                <MetaCell
                  icon={<Box className="w-3.5 h-3.5" />}
                  label={t("ai.meta.volume")}
                  value={analysis.meta.volumeCm3 ? `${analysis.meta.volumeCm3} cm³` : null}
                />
                <MetaCell
                  icon={<Layers3 className="w-3.5 h-3.5" />}
                  label={t("ai.meta.triangles")}
                  value={analysis.meta.triangles}
                />
                <MetaCell
                  icon={<Axis3d className="w-3.5 h-3.5" />}
                  label={t("ai.meta.orientation")}
                  value={translateAnalysisText(analysis.meta.orientation, language, t)}
                />
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className={`px-5 py-4 border-t bg-[#050505] ${pub.panelInnerDivide}`}>
            <div className="flex items-start gap-2.5 text-[11px] text-zinc-500 leading-relaxed">
              <Gauge className="w-3.5 h-3.5 mt-0.5 shrink-0 text-zinc-600" />
              <span>{t("ai.disclaimer")}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, accent, icon, multiline }) {
  return (
    <div className="px-5 py-3.5 grid grid-cols-5 gap-4 items-start">
      <div className="col-span-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2 pt-0.5">
        {icon}
        {label}
      </div>
      <div
        className={`col-span-3 min-w-0 break-words text-sm ${multiline ? "leading-relaxed" : "truncate"} ${
          accent === "good" ? "text-[#00FF9D]" : accent === "warn" ? "text-amber-300" : "text-zinc-200"
        }`}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function MetaCell({ icon, label, value }) {
  const has = value && value !== "—";
  return (
    <div className="bg-[#0C0C0F] p-3.5">
      <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
        <span className="text-[#FF4F00]">{icon}</span>
        {label}
      </div>
      <div className={`font-mono text-sm ${has ? "text-white" : "text-zinc-600"}`}>
        {has ? value : "—"}
      </div>
    </div>
  );
}
